import React, { useContext } from 'react';

import { Button } from '@mui/material';

import PostForm from './PostForm';
import PostUI from './PostUI';
import { POST_ITEMS_PER_PAGE } from '../../../apis/khoros/constants';
import { useKhorosPosts, useKhorosUser } from '../../../apis/khoros/hooks';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import I18nContext from '../../../aurr/contexts/I18nContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import LinkAsButton from '../../../helpers/LinkAsButton';
import Skeleton from '../skeleton';

export interface CommentsProps {
  commentCount?: number | null;
  userContentURL?: string;
  userProfileURL?: string;
  baseURL?: string;
  topicId: string;
}

export default function Comments({
  commentCount,
  userContentURL,
  userProfileURL,
  baseURL,
  topicId,
}: CommentsProps) {
  const { KHOROS_API } = useContext(ConfigContext);
  const { signIn, isLoggedIn, isUserLoading } = useContext(LoginContext);
  const topicProps = {
    baseURL: baseURL || KHOROS_API,
    topicId,
  };
  const { t } = useContext(I18nContext);
  const { data, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useKhorosPosts({
      ...topicProps,
      pageSize: POST_ITEMS_PER_PAGE,
    });

  const comments = data?.pages?.flatMap(page => page.kh_replies);

  const { data: khorosUser, isLoading: isKhorosUserLoading } = useKhorosUser({
    baseURL,
  });

  const { kh_user_id } = khorosUser || {};
  let discussionEntryContent;

  if (isUserLoading || isKhorosUserLoading) {
    discussionEntryContent = <Skeleton height="40px" width="100%" />;
  } else if (isLoggedIn && kh_user_id) {
    discussionEntryContent = (
      <PostForm
        baseURL={baseURL}
        topicId={topicId}
        userProfileURL={userProfileURL}
        userContentURL={userContentURL}
      />
    );
  } else {
    discussionEntryContent = (
      <LinkAsButton
        component="button"
        onClick={signIn}
        className="!au-text-white"
      >
        {t('comments.joinDiscussion', { ns: 'common' })}
      </LinkAsButton>
    );
  }

  return (
    <div className="!au-text-white">
      {discussionEntryContent}
      {commentCount ? (
        <p className="au-text-c16 au-font-bold au-leading-c24 au-mt-c18 au-mb-c40">
          {t('comments.commentsInDiscussion', {
            ns: 'common',
            count: commentCount,
          })}
        </p>
      ) : null}
      {comments && comments.length ? (
        <div className="au-flex au-flex-col au-gap-c24">
          {comments.map(comment => (
            <PostUI
              key={comment.kh_post_id}
              comment={comment}
              baseURL={baseURL}
              userContentURL={userContentURL}
              userProfileURL={userProfileURL}
              topicId={topicId}
            />
          ))}
          {isFetchingNextPage && (
            <div className="au-flex au-gap-2 au-items-start">
              <Skeleton height={60} width={60} rounded />
              <div className="au-flex au-flex-col au-grow au-gap-c8">
                <Skeleton height="15px" width="100%" />
                <Skeleton height="15px" width="100%" />
                <Skeleton height="15px" width="100%" />
              </div>
            </div>
          )}
          {(hasNextPage || (data?.pages && data.pages.length > 1)) && (
            <div className="au-pl-c68">
              <Button
                type="button"
                onClick={() => {
                  fetchNextPage();
                }}
                disabled={isFetchingNextPage || !hasNextPage}
              >
                {t('comments.loadMoreComments', { ns: 'common' })}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="au-text-c12">
          {t('comments.noComments', { ns: 'common' })}
        </p>
      )}
    </div>
  );
}
